import { useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { CollectionPageChildProps } from "../CollectionPage";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import MapsAutocomplete from "@/components/mapsautocomplete/MapsAutocomplete";
import { patchCollection } from "@/services/collection";
import { infoStepperOutletContext } from "../InfoStepper";

export default function HotelData({
  collection,
  onRefresh,
}: CollectionPageChildProps) {
  const { setProgress } = useOutletContext<infoStepperOutletContext>();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const formSchema = z.object({
    publicWebsite: z.string().min(5),
    publicEmail: z.string().email(t("auth.invalid_email")),
    phoneNumber: z.string().min(10, t("auth.valid_phone_number")),
    address: z.any(),
    cin: z.string().optional(),
    cir: z.string().optional(),
    numberOfRooms: z.number().min(1),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      publicWebsite: collection?.website || "",
      publicEmail: collection?.email || "",
      phoneNumber: collection?.phone || "",
      address: collection?.census?.location || "",
      cin: collection?.cin || "",
      cir: collection?.cir || "",
      numberOfRooms: collection?.preData?.numberOfRooms || NaN,
    },
  });

  useEffect(() => {
    setProgress(20);
    if (form.getValues("publicWebsite") !== "") {
      form.trigger("publicWebsite");
    }
  }, []);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    //check if data.publicWebsite has https or http prefix
    if (
      !data.publicWebsite.startsWith("http://") &&
      !data.publicWebsite.startsWith("https://")
    ) {
      data.publicWebsite = "https://" + data.publicWebsite;
    }

    const label = data.address?.label;
    const address_components = data.address?.location?.address_components
      ? Object.keys(data.address?.address_components).map(
          (i) => data.address?.address_components[i]
        )
      : [];
    const placeId = data.address?.value?.place_id;
    const address = {
      label: label,
      route: address_components?.find((a) => a.types.includes("route"))
        ?.long_name,
      streetNumber: address_components?.find((a) =>
        a.types.includes("street_number")
      )?.long_name,
      city: address_components?.find(
        (a) =>
          a.types.includes("administrative_area_level_3") ||
          a.types.includes("locality")
      )?.long_name,
      province: address_components?.find((a) =>
        a.types.includes("administrative_area_level_2")
      )?.short_name,
      country: address_components?.find((a) => a.types.includes("country"))
        ?.long_name,
      placeId: placeId,
    };

    try {
      await patchCollection({
        collectionId: collection?._id,
        preData: {
          numberOfRooms: data.numberOfRooms,
        },
        address,
        email: data.publicEmail,
        website: data.publicWebsite,
        phone: data.phoneNumber,
        cin: data.cin,
        cir: data.cir,
        census: {
          location: data.address,
        },
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      await onRefresh();
      navigate(`/collection/${collection?._id}/infos/hotelbookingengine`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mt-5 mb-10">
        <p
          className="text-lg text-gray-600"
          dangerouslySetInnerHTML={{ __html: t("onboarding.hotel_data_body") }}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="publicWebsite"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("collection.public_website")}*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="https://www.example.com"
                        onBlur={(e) => {
                          field.onBlur();
                          if (e.target.value) {
                            form.trigger("publicWebsite");
                          }
                        }}
                        onChange={(e) => {
                          field.onChange(e);
                          form.clearErrors("publicWebsite");
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t("collection.census_location")}*</FormLabel>
                    <FormControl>
                      <MapsAutocomplete
                        onSelect={(address: string) => {
                          field.onChange(address);
                        }}
                        defaultValue={field.value}
                        // findedAddress={findedHotelAddress}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="publicEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("onboarding.email")}*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="hotel@example.com"
                        onBlur={(e) => {
                          field.onBlur();
                          if (e.target.value) {
                            form.trigger("publicEmail");
                          }
                        }}
                        onChange={(e) => {
                          field.onChange(e);
                          form.clearErrors("publicEmail");
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("onboarding.phone")}*</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t("auth.phone_number_placeholder")}
                        onBlur={(e) => {
                          field.onBlur();
                          if (e.target.value) {
                            form.trigger("phoneNumber");
                          }
                        }}
                        onChange={(e) => {
                          // Allow digits and + for international prefix
                          const value = e.target.value.replace(/[^\d+]/g, "");
                          e.target.value = value;
                          field.onChange(value);
                          form.clearErrors("phoneNumber");
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="numberOfRooms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("collection.number_of_rooms")} {t("form.required")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step={1}
                        placeholder={t("collection.number_of_rooms")}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        value={field.value}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cin"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t("collection.cin")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("collection.cin_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cir"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t("collection.cir")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("collection.cir_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      </div>

      <div className="mt-10">
        <div className="flex flex-row justify-end">
          {!isLoading ? (
            <Button
              variant="takyon"
              type="submit"
              onClick={form.handleSubmit(onSubmit)}
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              {t("buttons.save_and_continue")}
            </Button>
          ) : (
            <Button variant="takyon" disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("buttons.loading")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
